import CloseIcon from 'assets/icons/close-dark.svg'
import FiltersIcon from 'assets/icons/filters.svg'
import { SearchParams } from 'backend/services/types/urApiService.types'
import classnames from 'classnames'
import LoadingBubbles from 'components/Animations/LoadingBubbles/LoadingBubbles'
import AriaModal from 'components/AriaModal/AriaModal'
import { FilterType, ToggleParams } from 'frontend/enums'
import { Filters as FiltersType } from 'frontend/types'
import { whenSlow } from 'frontend/utils/promiseHelpers'
import { useState } from 'react'
import MobileFilterItems from './MobileFilterItems'
import styles from './MobileFilters.module.css'
import ClearButton from './components/ClearButton/ClearButton'
import { ToggleEnableState } from './components/Toggle/Toggle'

interface Props {
  filters: FiltersType
  hiddenFilters?: FilterType[]
  searchParams: SearchParams
  totalCount: number
  updateSearch: (params: SearchParams, shouldReset?: boolean) => Promise<void>
  isFilterButtonHighlighted: boolean
}

const MobileFilters = ({
  filters,
  hiddenFilters,
  searchParams,
  totalCount,
  updateSearch,
  isFilterButtonHighlighted,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [openPanels, setOpenPanels] = useState<FilterType[]>([])
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false)
  const [animate, setAnimate] = useState(false)

  const handlePanelClick = (incomingType: FilterType) => {
    if (isModalOpen) {
      setOpenPanels(prevState =>
        prevState.includes(incomingType) ? [] : [incomingType],
      )
    } else {
      setOpenPanels(prevState =>
        prevState.includes(incomingType)
          ? [...prevState.filter(t => t !== incomingType)]
          : [...prevState, incomingType],
      )
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setAnimate(false)
    setOpenPanels([])
  }

  const executeSearch = async (
    params: SearchParams,
    shouldResetAllFilters = false,
  ) => {
    const updatePromise = updateSearch(params, shouldResetAllFilters)

    whenSlow(updatePromise, 400).then(
      isSlow => isSlow && setShowLoadingAnimation(true),
    )
    await updatePromise
    setShowLoadingAnimation(false)
  }

  const showAnimation = () => {
    setTimeout(() => {
      setAnimate(true)
    }, 100)
  }

  const handleClickSettingToggle = async (key: ToggleParams) => {
    const payload = searchParams[key] ? { [key]: null } : { [key]: true }

    setIsLoading(true)
    await executeSearch(payload)
    setIsLoading(false)
  }

  const getInteractiveState = (
    searchResultsExists: boolean,
  ): ToggleEnableState => {
    if (!searchResultsExists) {
      return ToggleEnableState.Disabled
    }

    if (showLoadingAnimation) {
      return ToggleEnableState.Disabled
    }
    if (isLoading) {
      return ToggleEnableState.NotInteractive
    }
    return ToggleEnableState.Enabled
  }

  const resetFilter = () => {
    executeSearch({}, true)
  }

  const buttonLabel = isModalOpen ? 'Stäng filter' : 'Öppna filter'

  const { languages } = filters

  return (
    <>
      <button
        aria-label={buttonLabel}
        disabled={!languages}
        className={classnames(styles.toggleButton, {
          [`${styles.toggleButtonHasFilters}`]: isFilterButtonHighlighted,
        })}
        data-testid="toggle-button"
        data-trk="search-page-filter-button"
        data-trk-has-filter={isFilterButtonHighlighted}
        onClick={() => {
          showAnimation()
          setIsModalOpen(true)
        }}
        type="button"
      >
        <FiltersIcon
          aria-hidden
          className={styles.toggleButtonIcon}
          focusable="false"
        />
        Filter
      </button>

      <AriaModal isModalOpen={isModalOpen} onModalClose={() => closeModal()}>
        <div
          className={classnames(styles.overlay, {
            [`${styles.overlayAnimation}`]: animate,
          })}
          data-testid="filters-overlay"
        >
          <div className={styles.overlayContent}>
            <div className={styles.closeButtonWrapper}>
              <button
                className={styles.closeButton}
                data-testid="close-button"
                data-trk="filter-close-button"
                onClick={() => {
                  closeModal()
                }}
                type="button"
              >
                <CloseIcon aria-hidden focusable="false" />
                Stäng
              </button>
            </div>

            <MobileFilterItems
              hiddenFilters={hiddenFilters || []}
              filters={filters}
              resetFilter={resetFilter}
              isInModal
              getInteractiveState={getInteractiveState}
              handleClickSettingToggle={handleClickSettingToggle}
              handlePanelClick={handlePanelClick}
              searchParams={searchParams}
              openPanels={openPanels}
              executeSearch={executeSearch}
            />

            <div className={styles.overlayFooter}>
              <div className={styles.overlayFooterButtonContainer}>
                <ClearButton resetFilter={resetFilter} isInModal />
                <button
                  className={classnames(styles.showButton, {
                    [`${styles.activeShowButton}`]: isFilterButtonHighlighted,
                  })}
                  data-testid="filter-show-result-button"
                  data-trk="filter-show-result-button"
                  onClick={() => {
                    closeModal()
                  }}
                  type="button"
                >
                  {showLoadingAnimation ? (
                    <LoadingBubbles />
                  ) : (
                    <span>{`Visa ${totalCount} träffar`}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AriaModal>
    </>
  )
}

export default MobileFilters
